// frontend/src/pages/SelfServicePolicy.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, Download, Printer, CheckCircle2, History,
  FileText, CalendarDays, UserCog, BadgeCheck, Scale, Megaphone, Users, MonitorCog,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { InfoBadge } from '../components/UI/InfoBadge';
import { Block } from '../components/handbook/DocumentBlocks';
import { usePolicy, useAcknowledgePolicy, useDownloadPolicyPdf } from '../hooks/usePoliciesData';

const CATEGORY = {
  ethics: { label: 'Ethics & Integrity', icon: Scale, text: 'text-amber-700', soft: 'bg-amber-50' },
  governance: { label: 'Governance & Speaking Up', icon: Megaphone, text: 'text-violet-700', soft: 'bg-violet-50' },
  workplace: { label: 'Workplace & People', icon: Users, text: 'text-teal-700', soft: 'bg-teal-50' },
  information_security: { label: 'Information Security & IT', icon: MonitorCog, text: 'text-sky-700', soft: 'bg-sky-50' },
  data_protection: { label: 'Data Protection & Privacy', icon: ShieldCheck, text: 'text-rose-700', soft: 'bg-rose-50' },
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const AckGate = ({ policy, onAcknowledge, isPending }) => {
  if (policy.acknowledged) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 bg-emerald-50/70">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-900">
            You acknowledged <strong>version {policy.version}</strong>
            {policy.acknowledgedAt ? ` on ${fmt(policy.acknowledgedAt)}` : ''}.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-amber-200 overflow-hidden">
      <div className="px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <BadgeCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-card-header">Please read and acknowledge this policy</h2>
            <p className="text-body mt-0.5">
              Version {policy.version}, effective {policy.effectiveDate}. By acknowledging, you confirm
              you have read and understood this policy.
            </p>
            <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary w-full sm:w-auto"
                onClick={onAcknowledge}
                disabled={isPending}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {isPending ? 'Recording…' : 'I have read and acknowledge'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SelfServicePolicy = () => {
  const { policyKey } = useParams();
  const { data: policy, isLoading, isError } = usePolicy(policyKey);
  const acknowledge = useAcknowledgePolicy();
  const downloadPdf = useDownloadPolicyPdf();

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="card"><div className="card-content py-20"><LoadingSpinner /></div></div>
      </DashboardLayout>
    );
  }

  if (isError || !policy) {
    return (
      <DashboardLayout>
        <div className="card">
          <div className="card-content text-center py-16">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <h3 className="text-subsection-header">Policy not found</h3>
            <p className="text-body mt-1">This policy may have been moved or unpublished.</p>
            <Link to="/self-service/policies" className="btn btn-primary mt-4 mx-auto inline-flex">
              Back to Company Policies
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const cat = CATEGORY[policy.category] || CATEGORY.ethics;
  const CatIcon = cat.icon;
  const history = policy.documentControl || [];
  const isMisPolicy = policy.owner?.toLowerCase().includes('management information system');
  const isQacPolicy = policy.owner?.toLowerCase().includes('quality assurance and compliance');

  const meta = [
    { icon: FileText, label: 'Version', value: policy.version },
    { icon: CalendarDays, label: 'Effective', value: policy.effectiveDate || '—' },
    { icon: UserCog, label: 'Owner', value: policy.owner || '—' },
    { icon: BadgeCheck, label: 'Approved by', value: policy.approverName || '—' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-16">
        {/* Document header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Heading row: the back button only anchors the badges + title + summary. */}
            <div className="flex items-start gap-4">
              <Link
                to="/self-service/policies"
                aria-label="Back to Company Policies"
                title="Back to Company Policies"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cat.soft} ${cat.text}`}>
                    <CatIcon className="w-3.5 h-3.5" /> {cat.label}
                  </span>
                  <InfoBadge
                    color="slate"
                    label={policy.classification}
                    title={`${policy.classification} — for company use only`}
                    className="ml-auto flex-shrink-0"
                  />
                </div>

                <h1 className="text-section-header mt-3">{policy.title}</h1>
                {policy.summary && <p className="text-body mt-1">{policy.summary}</p>}
              </div>
            </div>

            {/* Meta grid — standalone block, spans the full card width. */}
            <dl className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {meta.map((m) => (
                <div key={m.label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                  <dt className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <m.icon className="w-3.5 h-3.5 flex-shrink-0" /> {m.label}
                  </dt>
                  <dd className="text-sm font-semibold text-gray-900 mt-0.5 truncate" title={m.value}>{m.value}</dd>
                </div>
              ))}
            </dl>

            {/* Actions — standalone block, spans the full card width. */}
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              {policy.hasPdf && (
                <button
                  type="button"
                  onClick={() => downloadPdf.mutate({ policyKey: policy.key, title: policy.title, version: policy.version })}
                  className="btn btn-secondary self-start sm:self-auto"
                  disabled={downloadPdf.isPending}
                >
                  <Download className="w-4 h-4" />
                  {downloadPdf.isPending ? 'Preparing…' : 'Download signed copy'}
                </button>
              )}
              <button type="button" onClick={() => window.print()} className="btn btn-secondary self-start sm:self-auto">
                <Printer className="w-4 h-4" />
                Print / Save PDF
              </button>
            </div>
          </div>
        </div>

        {/* Acknowledgement gate */}
        <AckGate
          policy={policy}
          onAcknowledge={() => acknowledge.mutate(policy.key)}
          isPending={acknowledge.isPending}
        />

        {/* Policy content */}
        <article className="card">
          <div className="card-content">
            <div className="space-y-4">
              {(policy.blocks || []).map((b, i) => <Block key={i} block={b} />)}
            </div>

            {policy.approverName && (
              <div className="mt-8 pt-5 border-t border-gray-100">
                <p className="text-xs text-gray-400">Approved &amp; endorsed by</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5">{policy.approverName}</p>
                <p className="text-xs text-gray-500">
                  Effective {policy.effectiveDate}
                  {policy.documentRef ? ` · ${policy.documentRef}` : ''}
                </p>
              </div>
            )}
          </div>
        </article>

        {/* Bottom acknowledgement prompt (mirrors the gate so employees can sign off after reading) */}
        {!policy.acknowledged && (
          <div className="card border-amber-200">
            <div className="card-content flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-body">
                Finished reading? Confirm you have read and understood <strong>{policy.title}</strong>.
              </p>
              <button
                type="button"
                className="btn btn-primary w-full sm:w-auto flex-shrink-0"
                onClick={() => acknowledge.mutate(policy.key)}
                disabled={acknowledge.isPending}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {acknowledge.isPending ? 'Recording…' : 'I have read and acknowledge'}
              </button>
            </div>
          </div>
        )}

        {/* Version history (Document Control) */}
        {history.length > 0 && (
          <article className="card">
            <div className="card-content">
              <header className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-100">
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                  <History className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-subsection-header">Version history</h2>
                  <p className="text-small mt-1">Change history for this policy. Maintained by {policy.owner}.</p>
                </div>
              </header>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {history.map((row) => (
                  <div key={row.version} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-900 tabular-nums">
                        v{row.version}{row.isCurrent ? ' · current' : ''}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{row.date}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">Approved by {row.approver}</div>
                    {row.summary && <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">{row.summary}</p>}
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-4 font-semibold">Version</th>
                      <th className="py-2 pr-4 font-semibold">Effective</th>
                      <th className="py-2 pr-4 font-semibold">Approved by</th>
                      <th className="py-2 font-semibold">Summary of changes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((row) => (
                      <tr key={row.version} className="align-top">
                        <td className="py-3 pr-4 font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                          v{row.version}
                          {row.isCurrent && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                              current
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{row.date}</td>
                        <td className="py-3 pr-4 text-gray-600">{row.approver}</td>
                        <td className="py-3 text-gray-700">{row.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-small mt-4">
                {isMisPolicy ? (
                  <>Questions about this policy? Contact the Management Information System (MIS) department.</>
                ) : isQacPolicy ? (
                  <>
                    Questions about this policy or your personal data? Contact the Data Protection Officer at{' '}
                    <a
                      href="mailto:dpo@exeo-global.com"
                      className="text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded font-medium"
                    >
                      dpo@exeo-global.com
                    </a>.
                  </>
                ) : (
                  <>
                    Questions about this policy? Contact HR at{' '}
                    <a
                      href="mailto:hrm@winner-e.sg"
                      className="text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded font-medium"
                    >
                      hrm@winner-e.sg
                    </a>.
                  </>
                )}
              </p>
            </div>
          </article>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServicePolicy;
